import { ProductId } from '.';
import * as Iap from '.';
import createActions from '../config/actions/createActions';
import {
  action,
  asyncAction,
  PurchaseProviderCreditCardBrand,
  ValidatePurchaseArgs,
  ValidatePurchaseResult,
} from '@play-co/replicant';
import { getLocale } from '../getters/user';
import { AtoneServiceType, LOCAL_CURRENCY, webIAPPrices } from './iap.ruleset';
import { getStripePriceIdByProductId } from './iap.util';
import type { RedirectStateAnalytics } from 'src/game/iap/analytics/analytics.types';
import type { KomojuPaymentMethod } from 'src/game/iap/iap.types';
import {
  addPendingPayment,
  getAllPendingPayments,
  PendingPayment,
  updatePendingPaymentStatus,
} from './iap.pending.payments';
import {
  createReportableError,
  SerializableReplicantError,
  toSerializableReplicantError,
} from '../utils/errors';

type RevenueData = {
  revenueGrossLocal: number;
  revenueGrossUSD: number | undefined;
};

export const iapActions = createActions({
  asyncCreateKomojuPayment: asyncAction(
    async (
      state,
      args: {
        paymentToken: string;
        productId: ProductId;
        returnUrl?: string;
        analytics: {
          feature: string;
          subFeature: string;
        };
        webPayAnalytics: RedirectStateAnalytics;
        methodTimestamp: number;
      },
      api,
    ) => {
      const now = api.date.now();
      const amount = webIAPPrices[args.productId];

      // Get data from komoju
      const { payment } = await api.payments.createKomojuPayment({
        amount,
        currency: LOCAL_CURRENCY,
        paymentToken: args.paymentToken,
        productId: args.productId,
        locale: getLocale(state),
        returnUrl: args.returnUrl,
      });

      // Save consent for age verification
      if (state.ageConsentJPAccepted === false) {
        state.ageConsentJPAccepted = true;
      }

      /**
       * Supported credit card brands
       */
      const creditCardsMap: Record<string, PurchaseProviderCreditCardBrand> = {
        visa: 'visa',
        master: 'master',
        amex: 'amex',
        jcb: 'jcb',
        diners: 'diners',
        discover: 'discover',
      };

      // Detect payment method from analytics
      const paymentMethod = payment.paymentMethod;
      const creditCardBrand = payment.creditCardBrand
        ? creditCardsMap[payment.creditCardBrand]
        : undefined;

      // api.payments.createKomojuPayment returns a payment method without the prefix 'komoju_' so we need to add it here to not break the analytics
      // Keep in mind that state.pendingPayment use the payment method without the prefix
      const paymentMethodWithPrefix =
        `komoju_${paymentMethod}` as KomojuPaymentMethod;

      const pendingPayment = addPendingPayment(
        state,
        {
          paymentProvider: 'komoju',
          paymentMethod: paymentMethodWithPrefix,
          creditCardBrand,
          currency: LOCAL_CURRENCY,
          price: amount,
          purchaseId: payment.id,
          productId: args.productId,
          analytics: args.analytics,
          webPayAnalytics: args.webPayAnalytics,
          webPayMethodTimestamp: args.methodTimestamp,
        },
        now,
      );

      // If payment requires a redirect, bail. Rewards will be granted later.
      if (payment.status === 'pending' && payment.redirectUrl) {
        return {
          purchaseID: payment.id,
          redirectUrl: payment.redirectUrl,
          paymentMethod: paymentMethodWithPrefix,
          pendingPayment,
        };
      }

      // Resolve captured payments immediately.
      if (payment.status === 'captured') {
        // Otherwise immediately fulfill the purchase
        Iap.modifiers.fulfillPurchase(state, args.productId, payment.id, api, {
          sendPurchaseConfirmation: true,
        });

        // Update pending payment status
        pendingPayment.status = 'captured';

        // Return payment information
        return {
          purchaseID: payment.id,
          redirectUrl: undefined,
          paymentMethod: paymentMethodWithPrefix,
          creditCardBrand,
        };
      }

      // We expect only pending or captured payments
      throw new Error(`Unexpected payment status: ${payment.status}`, {
        cause: 'PROVIDER_ERROR',
      });
    },
  ),

  /**
   * Check and give rewards for pending payment
   * If payment was captured, give rewards and resolve pending payment
   *
   * @param payment Pending payment to check
   * @returns Captured pending payment
   * @throws Error if pending payment was not captured
   */
  fulfillPendingPayment: asyncAction(
    async (state, payment: PendingPayment, api): Promise<PendingPayment> => {
      const productId = payment.productId;

      // Check status of pending payment before resolve it
      const status = await api.asyncGetters.getPendingPaymentStatus({
        pendingPaymentId: payment.id,
      });

      // If pending payment was captured, resolve it and give rewards
      if (status === 'captured') {
        // Resolve pending status
        const capturedPayment = updatePendingPaymentStatus(
          state,
          {
            id: payment.id,
            status: 'captured',
          },
          api.date.now(),
        );

        // Give rewards
        Iap.modifiers.fulfillPurchase(
          state,
          productId,
          payment.purchaseId,
          api,
          { sendPurchaseConfirmation: true },
        );

        // Return captured product
        return capturedPayment;
      }

      // If pending payment was not actually captured, throw an error
      // Or it was not found in the state
      // Or it was already claimed
      throw new Error(
        `Can't fulfillPendingPayment. Pending payment not captured: ${payment.id} status: ${status}`,
      );
    },
  ),

  asyncHandlePurchase: asyncAction(
    async (
      state,
      args: {
        validationArgs: ValidatePurchaseArgs;
        revenueData: RevenueData;
        purchase: {
          currency: string;
          price: number;
          productId: string;
        };
        purchaseID: string;
      },
      api,
    ): Promise<ValidatePurchaseResult> => {
      try {
        const result = await api.purchases.validatePurchase(
          args.validationArgs,
          args.purchase,
        );

        // Verification failed
        if (!result.ok) {
          return result;
        }

        const productId = args.purchase.productId as ProductId;
        const { productIds, prices } = Iap.ruleset;
        if (!productIds.includes(productId)) {
          throw new Error('Unknown product: ' + productId);
        }

        const rewards = Iap.utils.getProductRewards(productId);
        const productPrice = prices[productId];

        Iap.modifiers.claimProductRewards(
          state,
          rewards,
          productId,
          args.purchaseID,
          api,
          { sendPurchaseConfirmation: true },
        );

        const { revenueGrossUSD } = args.revenueData;
        state.rulesetLTV += productPrice;
        state.actualLTV += revenueGrossUSD
          ? Math.round(revenueGrossUSD * 100)
          : productPrice;

        return { ok: true };
      } catch (error: any) {
        api.reportError(createReportableError('error', error), {
          purchaseID: args.purchaseID,
          productId: args.purchase.productId,
        });

        return {
          ok: false,
          errorCode: 'verificationFailed',
        };
      }
    },
  ),

  asyncCompleteStripePayment: asyncAction(async (state, _, api) => {
    const productId = state.checkoutProductId as ProductId;
    if (!productId) {
      throw new Error('Missing productId during asyncCreateStripePayment');
    }

    const { productIds, webIAPPrices } = Iap.ruleset;
    if (!productIds.includes(productId)) {
      throw new Error('Unknown product: ' + productId);
    }

    // Make sure that we have a payment intent session
    const paymentIntentId = state.checkoutSessionId;
    if (!paymentIntentId) {
      throw new Error(
        'Missing checkoutSessionId during asyncCreateStripePayment',
      );
    }

    const rewards = Iap.utils.getProductRewards(productId);
    const productPrice = webIAPPrices[productId];

    // Check payment status on server before we give any rewards
    const result = await api.asyncGetters.getStripePaymentIntentStatus({
      paymentIntentId,
    });

    if (!result) {
      throw new Error('Missing payment intent');
    }

    // Make sure that the payment intent was successful
    if (result.paymentStatus !== 'succeeded') {
      throw new Error(
        `Payment intent not succeeded, got: ${result.paymentStatus}`,
      );
    }

    Iap.modifiers.claimProductRewards(
      state,
      rewards,
      productId,
      paymentIntentId,
      api,
      { sendPurchaseConfirmation: true },
    );

    // TODO: figure out actualLTV
    state.actualLTV += productPrice;
    state.rulesetLTV += productPrice;
  }),

  /**
   * Clear Stripe payment intent in case of error to unlock user
   */
  clearStripePayment: action((state) => {
    state.checkoutProductId = undefined;
    state.checkoutSessionId = undefined;
  }),

  /**
   * Create komoju purchase session, for hosted fields API
   *
   * See: https://github.com/play-co/replicant/pull/1582
   */
  asyncCreateKomojuSession: asyncAction(
    async (
      state,
      args: {
        productId: ProductId;
        returnUrl: string;
      },
      api,
    ) => {
      const amount = webIAPPrices[args.productId];
      const returnUrl = args.returnUrl;

      const result = await api.payments.createKomojuSession({
        amount,
        currency: LOCAL_CURRENCY,
        returnUrl,
      });

      return result.session;
    },
  ),

  /**
   * Create stripe payment intent
   */
  asyncCreateStripePayment: asyncAction(
    async (state, args: { productId: string }, api) => {
      const productId = args.productId as ProductId;

      const priceId = getStripePriceIdByProductId(productId);
      const result = await api.payments.createPaymentIntent({
        priceId,
        productId,
      });

      // Save payment intent ID to check status later
      state.checkoutSessionId = result.paymentIntent.id;
      state.checkoutProductId = productId;

      // Return client secret to complete payment
      return {
        clientSecret: result.paymentIntent.clientSecret,
        purchaseID: result.paymentIntent.id,
      };
    },
  ),

  /**
   * Complete purchase with Atone payment service
   */
  asyncCompleteAtonePayment: asyncAction(
    async (
      state,
      opts: {
        transactionId: string;
        productId: ProductId;
        transactionType: AtoneServiceType;
      },
      api,
    ) => {
      const { transactionId, transactionType, productId } = opts;

      const transaction = await api.asyncGetters.getAtoneTransaction({
        id: transactionId,
        type: transactionType,
      });

      if (!transaction) {
        throw new Error('Missing Atone transaction');
      }

      if (transaction.isAuthorized === false) {
        throw new Error(
          `Atone transaction not authorized, code: ${transaction.authorizationErrorCode}`,
        );
      }

      // Save consent for age verification
      state.ageConsentJPAccepted = true;

      const rewards = Iap.utils.getProductRewards(productId);
      const productPrice = webIAPPrices[productId];

      Iap.modifiers.claimProductRewards(
        state,
        rewards,
        productId,
        transactionId,
        api,
        { sendPurchaseConfirmation: true },
      );

      // TODO: figure out actualLTV
      state.actualLTV += productPrice;
      state.rulesetLTV += productPrice;
    },
  ),

  /**
   * Cancel pending payment
   */
  asyncCancelPendingPayment: asyncAction(
    async (
      state,
      opts: {
        pendingPaymentId: string;
      },
      api,
    ) => {
      const paymentId = opts.pendingPaymentId;
      // We can cancel only pending payments
      const payment = getAllPendingPayments(state).find(
        (p) => p.id === paymentId,
      );

      // If payment not found, throw an error
      if (!payment) {
        throw new Error('Pending payment not found');
      }

      // Change the internal status of the payment to 'canceled'
      // Its not matter if we failed cancel it provider, we will report error and status
      updatePendingPaymentStatus(
        state,
        { id: payment.id, status: 'canceled' },
        api.date.now(),
      );

      // Try to cancel payment on provider, any way we will cancel it in our system
      const status = await api.asyncGetters.cancelPendingPayment({
        pendingPaymentId: payment.id,
      });

      // Return the status of the cancel operation, by this status we can check if it was canceled on provider or not
      // In case it's not equal to 'canceled' payment was not canceled on provider, we will report it to analytics and maybe have to cancel by hands
      return status;
    },
  ),

  /**
   * Set pending payment to failed state.
   * Failed payment won't be claimed by user and will be deleted after some time.
   */
  setPendingPaymentToFailedState: action(
    (
      state,
      opts: {
        pendingPaymentId: string;
      },
      api,
    ) => {
      const { pendingPaymentId } = opts;
      const payment = getAllPendingPayments(state).find(
        (p) => p.id === pendingPaymentId,
      );

      if (!payment) {
        throw new Error('Pending payment not found');
      }

      updatePendingPaymentStatus(
        state,
        { id: payment.id, status: 'failed' },
        api.date.now(),
      );
    },
  ),

  /**
   * Store the last payment method used by the user.
   */
  setLastPaymentMethod: action((state, paymentMethod: string) => {
    state.lastPaymentMethod = paymentMethod;
  }),
});
